import { API_URL } from "./api";

const DISCOVERY_PATH = "/api/v1/x402/discovery";
const REVIEW_DRAFT_PATH = "/api/v1/x402/review-draft";
const EVM_ADDRESS = /^0x[0-9a-fA-F]{40}$/;

export interface X402EvidenceReview {
  text: string;
  evidenceKind: string;
  reviewer: string | null;
  publishedAt: string | null;
  sourceUrl: string;
}

export interface X402EvidenceUsage {
  useCount: number;
  walletCount: number;
  profileCount: number;
}

export interface X402EvidencePrice {
  amount: string | null;
  currency: string | null;
  checkedAt: string | null;
  status: "unknown" | "stale";
}

export interface X402EvidenceInvocation {
  client: "agentcash";
  discoveryUrl: string;
  method: string;
  endpoint: string;
  requiresApproval: true;
}

export interface X402EvidenceCandidate {
  routeId: string;
  title: string;
  url: string;
  method: string;
  summary: string;
  matchReasons: string[];
  reviews: X402EvidenceReview[];
  usage: X402EvidenceUsage;
  price: X402EvidencePrice;
  invocation: X402EvidenceInvocation;
  links: { web: string; api: string };
}

export interface X402EvidenceDiscoveryResponse {
  version: "1";
  generatedAt: string;
  query: { q: string; limit: number; reviewer: string | null };
  candidates: X402EvidenceCandidate[];
  coverage: Record<string, unknown>;
}

export interface X402ReviewDraftResponse {
  ok: true;
  publish: false;
  requiresApproval: true;
  draft: { routeId: string; observation: string; evidenceKind: "user_observation" };
  limitations: string[];
}

export interface X402EvidenceDiscoveryInput {
  q?: string;
  limit?: number;
  reviewer?: string;
  signal?: AbortSignal;
}

/** Read the free, evidence-led discovery lane. This never invokes a service. */
export async function fetchX402EvidenceDiscovery(
  input: X402EvidenceDiscoveryInput = {},
): Promise<X402EvidenceDiscoveryResponse> {
  const params = new URLSearchParams();
  const query = input.q?.trim() ?? "";
  if (query) params.set("q", query);
  const limit = Math.min(Math.max(Math.trunc(input.limit ?? 10), 1), 20);
  params.set("limit", String(limit));
  const reviewer = input.reviewer?.trim() ?? "";
  if (reviewer) {
    if (!EVM_ADDRESS.test(reviewer)) throw new Error("Reviewer must be an EVM wallet address.");
    params.set("reviewer", reviewer);
  }

  const response = await fetch(`${API_URL}${DISCOVERY_PATH}?${params.toString()}`, {
    headers: { accept: "application/json" },
    signal: input.signal,
  });
  const value = await readResponse(response, DISCOVERY_PATH);
  if (!isDiscoveryResponse(value)) throw new Error("The evidence discovery response was malformed.");
  return value;
}

/** Turn a user's observation into copy only. This endpoint does not publish a review. */
export async function draftX402Review(input: {
  routeId: string;
  observation: string;
}): Promise<X402ReviewDraftResponse> {
  const routeId = input.routeId.trim();
  const observation = input.observation.trim();
  if (!routeId) throw new Error("Choose a route before drafting a review.");
  if (!observation) throw new Error("Describe what you observed before drafting a review.");
  const response = await fetch(`${API_URL}${REVIEW_DRAFT_PATH}`, {
    method: "POST",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify({ routeId, observation }),
  });
  const value = await readResponse(response, REVIEW_DRAFT_PATH);
  if (!isRecord(value) || value.ok !== true || value.publish !== false || value.requiresApproval !== true ||
      !isRecord(value.draft) || typeof value.draft.routeId !== "string" || typeof value.draft.observation !== "string" ||
      value.draft.evidenceKind !== "user_observation") {
    throw new Error("The review draft response was malformed.");
  }
  const limitations = Array.isArray(value.limitations) && value.limitations.every((item) => typeof item === "string")
    ? value.limitations
    : [];
  return {
    ok: true,
    publish: false,
    requiresApproval: true,
    draft: {
      routeId: value.draft.routeId as string,
      observation: value.draft.observation as string,
      evidenceKind: "user_observation",
    },
    limitations,
  };
}

async function readResponse(response: Response, path: string): Promise<unknown> {
  const value = await response.json().catch(() => null);
  if (!response.ok) {
    const detail = isRecord(value) && typeof value.error === "string" ? `: ${value.error}` : "";
    throw new Error(`${path} returned ${response.status}${detail}`);
  }
  return value;
}

function isDiscoveryResponse(value: unknown): value is X402EvidenceDiscoveryResponse {
  return isRecord(value) && value.version === "1" && typeof value.generatedAt === "string" &&
    !Number.isNaN(Date.parse(value.generatedAt)) && isQuery(value.query) &&
    Array.isArray(value.candidates) && value.candidates.every(isCandidate) && isRecord(value.coverage);
}

function isCandidate(value: unknown): value is X402EvidenceCandidate {
  if (!isRecord(value) || typeof value.routeId !== "string" || typeof value.title !== "string" ||
      typeof value.url !== "string" || typeof value.method !== "string" || typeof value.summary !== "string" ||
      !isStringArray(value.matchReasons) || !Array.isArray(value.reviews) || !value.reviews.every(isReview) ||
      !isUsage(value.usage) || !isPrice(value.price) ||
      !isInvocation(value.invocation) || !isRecord(value.links) || typeof value.links.web !== "string" ||
      typeof value.links.api !== "string") return false;
  return true;
}

function isReview(value: unknown): value is X402EvidenceReview {
  return isRecord(value) && typeof value.text === "string" && typeof value.evidenceKind === "string" &&
    (value.reviewer === null || typeof value.reviewer === "string") &&
    (value.publishedAt === null || (typeof value.publishedAt === "string" && !Number.isNaN(Date.parse(value.publishedAt)))) &&
    typeof value.sourceUrl === "string";
}

function isUsage(value: unknown): value is X402EvidenceUsage {
  return isRecord(value) && isCount(value.useCount) && isCount(value.walletCount) && isCount(value.profileCount);
}

function isPrice(value: unknown): value is X402EvidencePrice {
  return isRecord(value) && (value.amount === null || typeof value.amount === "string") &&
    (value.currency === null || typeof value.currency === "string") &&
    (value.checkedAt === null || (typeof value.checkedAt === "string" && !Number.isNaN(Date.parse(value.checkedAt)))) &&
    (value.status === "unknown" || value.status === "stale");
}

function isQuery(value: unknown): value is X402EvidenceDiscoveryResponse["query"] {
  return isRecord(value) && typeof value.q === "string" && isCount(value.limit) && value.limit >= 1 && value.limit <= 20 &&
    (value.reviewer === null || (typeof value.reviewer === "string" && EVM_ADDRESS.test(value.reviewer)));
}

function isInvocation(value: unknown): value is X402EvidenceInvocation {
  return isRecord(value) && value.client === "agentcash" && typeof value.discoveryUrl === "string" &&
    typeof value.method === "string" && typeof value.endpoint === "string" && value.requiresApproval === true;
}

function isRecord(value: unknown): value is Record<string, any> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function isCount(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}
